/* A top-up of any amount within billing's bounds. The payer names the money, never
   the credits: billing prices it, and the quote shows what it buys today. */
import { useEffect, useState } from "react";
import { useTopupQuote } from "@/shared/api/billing";
import { Row, GhostButton } from "@/shared/components/settings-atoms";
import { formatCredits } from "./format";

import type { CustomTopup as Bounds } from "@/shared/types/billing";

const MONEY = /^\d+(\.\d{1,2})?$/; // the API's own `Money` pattern
const QUOTE_DELAY_MS = 300;

/** The amount when billing would take it, else null. */
function valid(text: string, { min, max }: Bounds): string | null {
  const amount = text.trim();
  if (!MONEY.test(amount)) return null;
  const n = Number(amount);
  return n >= Number(min) && n <= Number(max) ? amount : null;
}

export default function CustomTopup({ bounds, disabled, onBuy }: {
  bounds: Bounds;
  disabled: boolean;
  onBuy: (amount: string) => void;
}) {
  const [text, setText] = useState("");
  const amount = valid(text, bounds);
  // One quote per pause in typing, not per keystroke.
  const [asked, setAsked] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setAsked(amount), QUOTE_DELAY_MS);
    return () => clearTimeout(t);
  }, [amount]);
  const { data: quote } = useTopupQuote(asked);
  const shown = amount && quote && asked === amount ? quote : null;

  const desc = shown
    ? <>{formatCredits(shown.credits)} credits{shown.bonus_credits > 0 && <> + {formatCredits(shown.bonus_credits)} bonus</>}</>
    : `Any amount from $${bounds.min} to $${bounds.max}`;

  return (
    <Row title="Custom amount" desc={desc}>
      <label className="flex items-center h-8 px-2.5 rounded-[10px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] focus-within:ring-[var(--hairline-focus)]">
        <span className="text-[13px] text-[var(--text-tertiary)]">$</span>
        <input
          aria-label="Custom top-up amount"
          inputMode="decimal"
          placeholder={String(bounds.min)}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && amount && !disabled && onBuy(amount)}
          className="w-20 pl-1 bg-transparent outline-none font-mono tabular-nums text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]"
        />
      </label>
      <GhostButton disabled={disabled || !amount} onClick={() => amount && onBuy(amount)} className="disabled:opacity-40 disabled:pointer-events-none">
        Buy
      </GhostButton>
    </Row>
  );
}
